import React, { useState, useCallback, useEffect, useRef, memo } from "react";

const DEBOUNCE_MS = 350;

/*
  The search pill shape (from zoomed Image 1):
  ┌──────────────────────╮         ╭──────────────────┐
  │  white input area    ╰─────────╯  orange section  │
  └──────────────────────╮         ╭──────────────────┘
                         └─────────┘
                          concave curve
                          (white scoops right, orange fills rest)

  Implementation:
  - The outer .search-pill is a white rounded pill (overflow:hidden)
  - Inside, we have: [white input flex:1] + [orange section]
  - The white section uses clip-path to cut a CONCAVE right edge
    so the white stops at a curved boundary
  - The orange section fills the remaining area with its own clip-path
    matching the concave left edge of that boundary
  - Together they look like ONE pill with a curved internal division

  The concave curve on the white section's right:
    clip-path: path from top-right curving INWARD (left) then back out
*/

export function OrangeCap({ height = 58, width: capWidth, children, className = "" }) {
  /*
    OrangeCap — SEMICIRCLE left edge
    The left boundary is a perfect half-circle arc that bulges
    rightward into the white pill area.

    Visual (pill cross-section):
      white ────────)  (──── orange ────╮
                    )  (                │
      white ────────)  (────────────────╯
                  semicircle (r = H/2)

    The arc peak is at x = r = H/2 pixels from the left edge.
    marginLeft = -r so the arc sits flush over the white pill end.
  */
  const W   = capWidth || 110;
  const H   = height;
  const mid = H / 2;
  const r   = mid;   // semicircle radius = half the pill height (perfect circle)

  /*
    Left edge = perfect semicircle arc bulging RIGHT into white area.
    The arc goes from (0, 0) → peak at (r, mid) → back to (0, H).
    This is a clean SVG arc: radius=r, sweep clockwise.

    Right end = fully rounded pill cap (same semicircle, other side).
  */
  const orangePath = [
    `M 0 0`,
    `A ${r} ${r} 0 0 1 0 ${H}`,        // left semicircle arc (convex, bulges right)
    `L ${W - r} ${H}`,                   // bottom edge
    `A ${r} ${r} 0 0 0 ${W - r} 0`,    // right semicircle arc (rounded cap)
    `Z`,
  ].join(" ");

  return (
    <div
      className={`pill-cap ${className}`}
      style={{
        width: W,
        height: H,
        flexShrink: 0,
        marginLeft: -(r),               // overlap white pill by r px so semicircle sits flush
        position: "relative",
        zIndex: 1,
        clipPath: `path('${orangePath}')`,
        background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingLeft: r + 8,             // push icons past the semicircle peak
        paddingRight: 8,
        gap: 10,
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
}

function NormalSearch({ onSearch }) {
  const [text,   setText]   = useState("");
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    const trimmed = text.trim();
    if (!trimmed) { onSearch?.(null); return; }
    timerRef.current = setTimeout(() => onSearch?.(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [text, onSearch]);

  const handleSearch = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    clearTimeout(timerRef.current);
    onSearch?.(trimmed);
  }, [text, onSearch]);

  const handleClear = useCallback(() => {
    setText("");
    onSearch?.(null);
    inputRef.current?.focus();
  }, [onSearch]);

  return (
    <div className="search-pill">

      {/* White input area */}
      <div className="pill-content">
        <div className="input-wrapper">
          <input
            ref={inputRef}
            className="pill-input"
            placeholder="Enter location to search manually"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            autoComplete="off"
            aria-label="Search properties by location"
          />
          {text && (
            <button className="clear-btn" onClick={handleClear}
              aria-label="Clear" type="button">✕</button>
          )}
        </div>
      </div>

      {/* Orange concave-left cap */}
      <OrangeCap height={58}>
        <button className="pill-btn" onClick={handleSearch}
          type="button" aria-label="Search">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
            stroke="white" strokeWidth="2.3"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7.5" />
            <line x1="16.5" y1="16.5" x2="22" y2="22" />
          </svg>
        </button>
      </OrangeCap>

    </div>
  );
}

export default memo(NormalSearch);